import React, { useContext, useEffect } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';

import { TextButton } from '@xingternal/button';
import { Card } from '@xingternal/card';
import { Carousel } from '@xingternal/carousel';
import { IconArrowRight } from '@xingternal/icons';
import { spaceL, spaceM } from '@xingternal/tokens';
import { BodyCopy, Headline } from '@xingternal/typography';
import { Column } from '@xingternal/xing-grid';

import CompanyContact, { addContactTracking } from '@/components/company-contact';
import { JobPostingModule } from '@/components/job-posting-module';
import { useJobPostingModuleDispatcher } from '@/components/job-posting/contexts';
import { JDPModuleName } from '@/components/job-posting/types';
import { JobDetailContext } from '@/contexts';
import { JobContactsQuery } from '@/graphql/generated';
import { useBreakpoints } from '@/hooks';

import { ScrollTrackingModuleWrapper } from '../scroll-tracking-module-wrapper';
import { filterCompanyContacts } from './filterCompanyContacts';

import styles from './company-contacts-module.css';

type Connections = JobContactsQuery['viewer'];

type Props = {
  contacts: Connections;
  company: string;
  moduleName: JDPModuleName;
};

const countContacts = (contacts: Connections): number =>
  Object.values(contacts ?? {})
    .filter((connection) => connection && typeof connection === 'object' && connection.edges)
    .reduce(
      (acc, connection) => (connection && typeof connection === 'object' ? acc + connection?.edges.length : acc),
      0
    );

const hasNextPages = (contacts: Connections): boolean =>
  Object.values(contacts ?? {})
    .filter((connection) => connection && typeof connection === 'object' && connection.pageInfo)
    .reduce(
      (acc, connection) =>
        connection && typeof connection === 'object' ? acc || connection?.pageInfo.hasNextPage : acc,
      false
    );

const decorateContactWithModuleTracking = addContactTracking('jobs_profile_other_visit_contact_module');

const CompanyContactsModule: React.FC<Props & WrappedComponentProps> = ({ contacts, intl, company, moduleName }) => {
  const { isMobile } = useBreakpoints();
  const { slug } = useContext(JobDetailContext);
  const filteredContacts = filterCompanyContacts(contacts, isMobile ? true : false);
  const hasEmptyContacts = !filteredContacts.length;
  const dispatch = useJobPostingModuleDispatcher();

  useEffect(() => {
    dispatch({
      type: 'UPDATE_MODULE_STATUS',
      moduleName,
      status: hasEmptyContacts ? 'HIDDEN' : 'VISIBLE',
    });
  }, [dispatch, hasEmptyContacts, moduleName]);

  const title = intl.formatMessage({ id: 'JOBS_DETAIL_COMPANY_CONTACTS_MODULE_TITLE' });
  const subtitle = intl.formatMessage({ id: 'JOBS_DETAIL_COMPANY_CONTACTS_MODULE_SUBTITLE' });

  const shouldDisplayShowMore =
    (countContacts(contacts) > filteredContacts.length || hasNextPages(contacts)) && !isMobile;

  const header = !isMobile ? (
    <Headline size="large" className={styles.title}>{`${title} ${subtitle}`}</Headline>
  ) : (
    <>
      <Headline size="large">{title}</Headline>
      <BodyCopy size="medium">{subtitle}</BodyCopy>
    </>
  );

  return hasEmptyContacts ? null : (
    <ScrollTrackingModuleWrapper moduleTrackingName={moduleName}>
      <JobPostingModule data-testid="company-contacts-module">
        <Column size={12}>{header}</Column>
        {isMobile ? (
          <Column size={12}>
            <Carousel itemSpacing={spaceM} paddingBottom={spaceL} itemSnapPosition="left">
              {filteredContacts.map((contact) =>
                contact.user ? (
                  <Card key={contact.user.id} minHeight="100%">
                    <CompanyContact contact={decorateContactWithModuleTracking(contact)} company={company} />
                  </Card>
                ) : null
              )}
            </Carousel>
          </Column>
        ) : (
          <>
            {filteredContacts.map((contact) =>
              contact.user ? (
                <Column key={contact.user.id} size={contact?.user?.connectionDegree === 1 ? 4 : 8}>
                  <CompanyContact contact={decorateContactWithModuleTracking(contact)} company={company} />
                </Column>
              ) : null
            )}
          </>
        )}
        {shouldDisplayShowMore && (
          <Column size={12} className={styles.showMoreFooter} data-testid="company-contacts-module-show-more">
            <TextButton
              small="medium"
              icon={IconArrowRight}
              iconAfter
              to={`/jobs/${slug}/contacts`}
              className={styles.showMoreLink}
            >
              <FormattedMessage id="JOBS_DETAIL_COMPANY_CONTACTS_MODULE_SHOW_ALL" />
            </TextButton>
          </Column>
        )}
      </JobPostingModule>
    </ScrollTrackingModuleWrapper>
  );
};

export default injectIntl(CompanyContactsModule);
